import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import analytics, { type PentictonEventsTracking } from '../lib/analytics';
import { useAuth } from '../app/providers/AuthProvider';
import { useConsent } from './useConsent';

/**
 * Hook for analytics tracking with consent management
 */
export const useAnalytics = () => {
  const location = useLocation();
  const { user, profile } = useAuth();
  const { hasAnalyticsConsent } = useConsent();

  // Track page views on route changes
  useEffect(() => {
    if (hasAnalyticsConsent()) {
      const title = document.title;
      const path = location.pathname + location.search;
      analytics.trackPageView(path, title, user?.uid);
    }
  }, [location, user?.uid, hasAnalyticsConsent]);

  // Set user properties when user changes
  useEffect(() => {
    if (hasAnalyticsConsent() && user && profile) {
      analytics.setUserProperties({
        userId: user.uid,
        isAdmin: profile.isAdmin,
        joinDate: profile.createdAt || new Date().toISOString(),
      });
    }
  }, [user, profile, hasAnalyticsConsent]);

  /**
   * Track a custom event
   */
  const trackEvent = useCallback(
    <T extends keyof PentictonEventsTracking>(
      eventName: T,
      properties: PentictonEventsTracking[T],
    ) => {
      if (hasAnalyticsConsent()) {
        analytics.trackEvent(eventName, properties);
      }
    },
    [hasAnalyticsConsent],
  );

  /**
   * Track RSVP actions
   */
  const trackRSVP = useCallback(
    (
      meetingId: string,
      groupId: string,
      meetingTitle: string,
      status: 'yes' | 'no' | 'maybe',
    ) => {
      trackEvent('rsvp_meeting', {
        meeting_id: meetingId,
        group_id: groupId,
        meeting_title: meetingTitle,
        meeting_date: new Date().toISOString(),
        rsvp_status: status,
      });
    },
    [trackEvent],
  );

  /**
   * Track RSVP cancellation
   */
  const trackCancelRSVP = useCallback(
    (meetingId: string, groupId: string, meetingTitle: string) => {
      trackEvent('cancel_rsvp', {
        meeting_id: meetingId,
        group_id: groupId,
        meeting_title: meetingTitle,
      });
    },
    [trackEvent],
  );

  /**
   * Track group joining
   */
  const trackJoinGroup = useCallback(
    (groupId: string, groupName: string, groupCategory?: string) => {
      trackEvent('join_group', {
        group_id: groupId,
        group_name: groupName,
        group_category: groupCategory,
      });
    },
    [trackEvent],
  );

  /**
   * Track group leaving
   */
  const trackLeaveGroup = useCallback(
    (groupId: string, groupName: string) => {
      trackEvent('leave_group', {
        group_id: groupId,
        group_name: groupName,
      });
    },
    [trackEvent],
  );

  /**
   * Track content views
   */
  const trackViewMeeting = useCallback(
    (meetingId: string, groupId: string, meetingTitle: string, fromPage?: string) => {
      trackEvent('view_meeting_details', {
        meeting_id: meetingId,
        group_id: groupId,
        meeting_title: meetingTitle,
        from_page: fromPage,
      });
    },
    [trackEvent],
  );

  /**
   * Track group views
   */
  const trackViewGroup = useCallback(
    (groupId: string, groupName: string, fromPage?: string) => {
      trackEvent('view_group_details', {
        group_id: groupId,
        group_name: groupName,
        from_page: fromPage,
      });
    },
    [trackEvent],
  );

  /**
   * Track sharing actions
   */
  const trackShareMeeting = useCallback(
    (meetingId: string, method: 'link' | 'email' | 'social') => {
      trackEvent('share_meeting', {
        meeting_id: meetingId,
        method,
      });
    },
    [trackEvent],
  );

  /**
   * Track sharing actions for groups
   */
  const trackShareGroup = useCallback(
    (groupId: string, method: 'link' | 'email' | 'social') => {
      trackEvent('share_group', {
        group_id: groupId,
        method,
      });
    },
    [trackEvent],
  );

  /**
   * Track search actions
   */
  const trackSearch = useCallback(
    (query: string, type: 'groups' | 'meetings', resultsCount: number) => {
      if (type === 'groups') {
        trackEvent('search_groups', {
          query,
          results_count: resultsCount,
        });
      } else {
        trackEvent('search_meetings', {
          query,
          results_count: resultsCount,
        });
      }
    },
    [trackEvent],
  );

  /**
   * Track filtering actions
   */
  const trackFilter = useCallback(
    (filters: string[], resultsCount: number) => {
      trackEvent('filter_groups', {
        filters,
        results_count: resultsCount,
      });
    },
    [trackEvent],
  );

  /**
   * Track authentication events
   */
  const trackSignUp = useCallback(
    (method: 'google' | 'email', role?: 'member' | 'organizer') => {
      trackEvent('sign_up', {
        method,
        user_role: role,
      });
    },
    [trackEvent],
  );

  /**
   * Track sign-in events
   */
  const trackSignIn = useCallback(
    (method: 'google' | 'email') => {
      trackEvent('sign_in', {
        method,
      });
    },
    [trackEvent],
  );

  /**
   * Track sign-out events
   */
  const trackSignOut = useCallback(() => {
    trackEvent('sign_out', {});
  }, [trackEvent]);

  /**
   * Track organizer actions
   */
  const trackCreateMeeting = useCallback(
    (
      meetingId: string,
      groupId: string,
      meetingType: 'in-person' | 'virtual' | 'hybrid',
    ) => {
      trackEvent('create_meeting', {
        meeting_id: meetingId,
        group_id: groupId,
        meeting_type: meetingType,
      });
    },
    [trackEvent],
  );

  /**
   * Track meeting updates
   */
  const trackUpdateMeeting = useCallback(
    (meetingId: string, groupId: string, changes: string[]) => {
      trackEvent('update_meeting', {
        meeting_id: meetingId,
        group_id: groupId,
        changes,
      });
    },
    [trackEvent],
  );

  /**
   * Track broadcast sending
   */
  const trackSendBroadcast = useCallback(
    (
      groupId: string,
      recipientCount: number,
      messageType: 'announcement' | 'reminder' | 'update',
    ) => {
      trackEvent('send_broadcast', {
        group_id: groupId,
        recipient_count: recipientCount,
        message_type: messageType,
      });
    },
    [trackEvent],
  );

  /**
   * Track milestone events
   */
  const trackFirstRSVP = useCallback(
    (meetingId: string, daysSinceSignup: number) => {
      trackEvent('first_rsvp', {
        meeting_id: meetingId,
        days_since_signup: daysSinceSignup,
      });
    },
    [trackEvent],
  );

  /**
   * Track role changes
   */
  const trackBecameOrganizer = useCallback(
    (daysSinceSignup: number) => {
      trackEvent('became_organizer', {
        days_since_signup: daysSinceSignup,
      });
    },
    [trackEvent],
  );

  return {
    // Core tracking
    trackEvent,

    // RSVP tracking
    trackRSVP,
    trackCancelRSVP,

    // Group tracking
    trackJoinGroup,
    trackLeaveGroup,
    trackViewGroup,
    trackShareGroup,

    // Meeting tracking
    trackViewMeeting,
    trackShareMeeting,
    trackCreateMeeting,
    trackUpdateMeeting,

    // Discovery tracking
    trackSearch,
    trackFilter,

    // Auth tracking
    trackSignUp,
    trackSignIn,
    trackSignOut,

    // Organizer tracking
    trackSendBroadcast,

    // Milestone tracking
    trackFirstRSVP,
    trackBecameOrganizer,
  };
};
